import useClientContext, { setModal } from "@hooks/useClientContext";
import useGame from "@hooks/useGame";
import useGigya from "@hooks/useGigya";
import useSession from "@hooks/useSession";
import { breakpoints, media, publicBasePath, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
import EntryBarListBox from "./EntryBarListBox";

const EntryBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-height: ${pxToRem(56)};
  background: white;

  ${media.tablet} {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const TeamContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 100%;
  min-width: 0;

  ${media.tablet} {
    padding-top: ${pxToRem(11)};
  }
`;

const TeamLogoContainer = styled.div`
  margin: 0 ${pxToRem(8)} 0 ${pxToRem(26)};
  flex: 0 0 ${pxToRem(36)};
  height: ${pxToRem(36)};
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: 100%;
  }

  ${media.tablet} {
    margin-left: 1rem;
    flex: 0 0 ${pxToRem(30)};
    height: ${pxToRem(30)};
  }
`;

const NameContainer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  padding-right: ${pxToRem(8)};
  position: relative;

  color: black;
  font-family: ${({ theme }) => theme.fontSecondary};
  font-size: ${pxToRem(28)};
  letter-spacing: 0px;
  line-height: ${pxToRem(22)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.tablet} {
    font-size: ${pxToRem(21)};
    line-height: ${pxToRem(16.21)};
  }
`;

const CreateEntryButton = styled.button`
  background: transparent;
  border: none;
  margin: 0 ${pxToRem(8)};
  padding: ${pxToRem(4)} 0;
  outline-offset: ${pxToRem(-2)};
  ${media.tablet} {
    margin-left: 0;
    padding-right: ${pxToRem(4)};
  }
`;

const RoundPointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  padding: 0 ${pxToRem(20)} 0;
  margin: ${pxToRem(10)} 0;
  position: relative;
  flex: 0 1 auto;

  &:not(:last-of-type) ::after {
    content: "";
    position: absolute;
    right: 0px;
    top: calc(50% - ${pxToRem(13)});
    height: ${pxToRem(26)};
    width: ${pxToRem(1)};

    transform: skew(-20deg);
    background-color: black;
  }

  &:nth-last-of-type(2) ::after {
    background-color: ${({ theme }) => theme.blue};
  }

  ${media.tablet} {
    flex: 0 1 ${pxToRem(145)};
    padding: 0 ${pxToRem(12)} 0 ${pxToRem(10)};
    margin: 0 0 ${pxToRem(11)} 0;
  }

  &:first-of-type {
    @media (min-width: ${breakpoints.tablet}) {
      padding-left: 0;
    }
  }
`;

const RoundPoints = styled.div`
  text-transform: uppercase;
  letter-spacing: 0px;
  white-space: nowrap;

  &.roundName {
    color: ${({ theme }) => theme.grey_200};
    font-size: ${pxToRem(10)};
    ${media.tablet} {
      font-size: ${pxToRem(8)};
      height: ${pxToRem(12)};
    }
  }
  &.points {
    color: black;
    font-size: ${pxToRem(18)};
    font-weight: 900;
  }
`;

const PointsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex: 0 1 ${pxToRem(574)};

  ${media.tablet} {
    height: 100%;
    overflow-x: auto;
    flex: 1 1 auto;
  }
`;

const NextRaceContainer = styled.div`
  flex: 1 0 ${pxToRem(122)};
  margin: ${pxToRem(10)} ${pxToRem(32)} 0 ${pxToRem(16)};
  ${media.tablet} {
    display: none;
  }
`;
const NextRaceBroadcastLogos = styled.div`
  display: flex;
  justify-content: space-between;
  min-height: ${pxToRem(16)};
  img {
    max-height: ${pxToRem(16)};
    width: auto;
    height: auto;
    user-select: none;
    pointer-events: none;
    &:first-of-type {
      margin-right: ${pxToRem(8)};
    }
  }
`;
const NextRaceLabel = styled.div`
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ theme }) => theme.grey_200};
  font-size: ${pxToRem(10)};
  margin-bottom: ${pxToRem(4)};
`;

const EntryBar = ({ isLeagueMemberView, leagueMemberEntry }) => {
  const { data: session } = useSession();
  const [{ currentEntry }, dispatch] = useClientContext();
  const { showAuthModal } = useGigya();
  const { data: game } = useGame();

  const isLoggedIn = Boolean(session.user.token);
  const entryCount = session.user.chase_entries_count ?? 0;
  const avatarId = isLeagueMemberView
    ? leagueMemberEntry?.avatar_id
    : currentEntry?.avatar_id ?? 50;

  const current_round = game.state?.current_round ?? 1;
  const current_race_index = game.state?.current_race_index ?? 0;

  const currentRaceId = game.rounds?.[current_round - 1]?.races?.[current_race_index];
  const race = game?.races?.[currentRaceId];
  const nextRaceCity = race?.track_name;
  const radioBroadcaster = race?.radio_broadcaster;
  const tvBroadcaster = race?.television_broadcaster;

  const round1Pts = isLeagueMemberView
    ? leagueMemberEntry?.points?.round_1 ?? "- -"
    : currentEntry?.entry_bar?.points?.round_1 ?? "- -";
  const round2Pts = isLeagueMemberView
    ? leagueMemberEntry?.points?.round_2 ?? "- -"
    : currentEntry?.entry_bar?.points?.round_2 ?? "- -";
  const round3Pts = isLeagueMemberView
    ? leagueMemberEntry?.points?.round_3 ?? "- -"
    : currentEntry?.entry_bar?.points?.round_3 ?? "- -";
  const round4Pts = isLeagueMemberView
    ? leagueMemberEntry?.points?.round_4 ?? "- -"
    : currentEntry?.entry_bar?.points?.round_4 ?? "- -";
  const totalPts = isLeagueMemberView
    ? leagueMemberEntry?.points?.total_points ?? "- -"
    : currentEntry?.entry_bar?.points?.total_points ?? "- -";

  return (
    <EntryBarContainer>
      <TeamContainer>
        <TeamLogoContainer>
          <img
            src={`${publicBasePath}/images/avatar/${avatarId}.png`}
            alt="Entry Avatar"
            width="36"
            height="36"
          />
        </TeamLogoContainer>
        <NameContainer>
          {entryCount > 0 ? (
            <EntryBarListBox
              isLeagueMemberView={isLeagueMemberView}
              leagueMemberEntry={leagueMemberEntry}
            />
          ) : (
            <CreateEntryButton
              onClick={isLoggedIn ? () => dispatch(setModal("CREATE_ENTRY")) : showAuthModal}
            >
              Create an Entry
            </CreateEntryButton>
          )}
        </NameContainer>
      </TeamContainer>
      <PointsContainer>
        <RoundPointsContainer>
          <RoundPoints className="roundName">Round of 12</RoundPoints>
          <RoundPoints className="points">{round1Pts}</RoundPoints>
        </RoundPointsContainer>

        <RoundPointsContainer>
          <RoundPoints className="roundName">Round of 8</RoundPoints>
          <RoundPoints className="points">{round2Pts}</RoundPoints>
        </RoundPointsContainer>

        <RoundPointsContainer>
          <RoundPoints className="roundName">Championship</RoundPoints>
          <RoundPoints className="points">{round3Pts}</RoundPoints>
        </RoundPointsContainer>

        <RoundPointsContainer>
          <RoundPoints className="roundName">Champion</RoundPoints>
          <RoundPoints className="points">{round4Pts}</RoundPoints>
        </RoundPointsContainer>

        <RoundPointsContainer>
          <RoundPoints className="roundName">Total Points</RoundPoints>
          <RoundPoints className="points">{totalPts}</RoundPoints>
        </RoundPointsContainer>
      </PointsContainer>

      <NextRaceContainer>
        <NextRaceLabel>Next Race: {nextRaceCity}</NextRaceLabel>
        <NextRaceBroadcastLogos>
          {race && (
            <>
              <img
                src={`${publicBasePath}/images/tvradio/${tvBroadcaster}.png`}
                alt={`TV: ${tvBroadcaster}`}
                height="16"
              />
              <img
                src={`${publicBasePath}/images/tvradio/${radioBroadcaster}.png`}
                alt={`Radio: ${radioBroadcaster}`}
                height="16"
              />
            </>
          )}
        </NextRaceBroadcastLogos>
      </NextRaceContainer>
    </EntryBarContainer>
  );
};

export default EntryBar;
